import React, { useState, useEffect, useCallback } from 'react';
import { useUser } from '../../shared/context/UserContext';
import { apiFetch } from '../../auth/apiFetch';
import './ProjectsScreen.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:7071/api';

/* ── Fetch helpers ────────────────────────────────────────────────────────── */

async function fetchProjects(callerEmail) {
  const res = await apiFetch(
    `${API_BASE_URL}/get_projects?caller_email=${encodeURIComponent(callerEmail)}`
  );
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

async function fetchSites(callerEmail) {
  const res = await apiFetch(
    `${API_BASE_URL}/get_sites?caller_email=${encodeURIComponent(callerEmail)}`
  );
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

async function createProject(payload, callerEmail) {
  const res = await apiFetch(
    `${API_BASE_URL}/manage_project?caller_email=${encodeURIComponent(callerEmail)}`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }
  );
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    const err = new Error(data.error || `HTTP ${res.status}`);
    err.status = res.status;
    throw err;
  }
  return res.json();
}

async function updateProject(payload, callerEmail) {
  const res = await apiFetch(
    `${API_BASE_URL}/manage_project?caller_email=${encodeURIComponent(callerEmail)}`,
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    }
  );
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    const err = new Error(data.error || `HTTP ${res.status}`);
    err.status = res.status;
    throw err;
  }
  return res.json();
}

/* ── Status badge ─────────────────────────────────────────────────────────── */

function StatusBadge({ status }) {
  const cls = {
    SETUP: 'ps-badge-setup',
    ACTIVE: 'ps-badge-active',
    PAUSED: 'ps-badge-paused',
  }[status] || 'ps-badge-setup';
  return <span className={`ps-badge ${cls}`}>{status}</span>;
}

/* ── Project Form (create + edit) ─────────────────────────────────────────── */

function ProjectForm({ sites, callerEmail, editProject, onDone, onCancel }) {
  const isEdit = !!editProject;
  const [form, setForm] = useState(
    isEdit
      ? {
          project_name: editProject.project_name || '',
          client_oem: editProject.client_oem || '',
          site_id: editProject.site_id || '',
          workflow_type: editProject.workflow_type || 'LIFECYCLE',
          status: editProject.status || 'SETUP',
          start_mode: editProject.start_mode || 'SEEDED',
        }
      : {
          project_name: '',
          client_oem: '',
          site_id: '',
          workflow_type: 'LIFECYCLE',
          status: 'SETUP',
          start_mode: 'SEEDED',
        }
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  function set(field, value) {
    setForm(prev => ({ ...prev, [field]: value }));
  }

  // Auto-fill client_oem from site selection
  function handleSiteChange(siteId) {
    set('site_id', siteId);
    const site = sites.find(s => s.site_id === siteId || s.id === siteId);
    if (site && site.oem_primary) {
      set('client_oem', site.oem_primary.toUpperCase());
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.project_name || !form.client_oem || !form.site_id || !form.workflow_type) {
      setError('All fields are required.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      if (isEdit) {
        await updateProject({
          project_id: editProject.id,
          project_name: form.project_name,
          status: form.status,
          start_mode: form.start_mode,
        }, callerEmail);
      } else {
        await createProject(form, callerEmail);
      }
      onDone();
    } catch (err) {
      if (err.status === 409) {
        setError('A project already exists for this site and workflow type.');
      } else {
        setError(isEdit ? 'Failed to save changes. Please try again.' : 'Failed to create project. Please try again.');
      }
    } finally {
      setSaving(false);
    }
  }

  return (
    <form className="ps-form" onSubmit={handleSubmit}>
      <h3 className="ps-form-title">{isEdit ? 'Edit Project' : 'New Project'}</h3>

      <label className="ps-label">
        Project Name
        <input
          className="ps-input"
          value={form.project_name}
          onChange={e => set('project_name', e.target.value)}
          placeholder="e.g. Autoliv Ogden — Lifecycle"
        />
      </label>

      <label className="ps-label">
        Site
        <select
          className="ps-input"
          value={form.site_id}
          onChange={e => handleSiteChange(e.target.value)}
          disabled={isEdit}
        >
          <option value="">— Select a site —</option>
          {sites.map(s => (
            <option key={s.site_id || s.id} value={s.site_id || s.id}>
              {s.site_name} {s.site_city ? `(${s.site_city})` : ''}
            </option>
          ))}
        </select>
      </label>

      <label className="ps-label">
        Client OEM
        <input
          className="ps-input"
          value={form.client_oem}
          onChange={e => set('client_oem', e.target.value.toUpperCase())}
          placeholder="e.g. AUTOLIV"
          disabled={isEdit}
        />
      </label>

      <label className="ps-label">
        Workflow Type
        <select
          className="ps-input"
          value={form.workflow_type}
          onChange={e => set('workflow_type', e.target.value)}
          disabled={isEdit}
        >
          <option value="LIFECYCLE">LIFECYCLE</option>
          <option value="DISPOSAL">DISPOSAL</option>
        </select>
      </label>

      <label className="ps-label">
        Start Mode
        <select
          className="ps-input"
          value={form.start_mode}
          onChange={e => set('start_mode', e.target.value)}
        >
          <option value="SEEDED">SEEDED</option>
          <option value="BLANK_SLATE">BLANK_SLATE</option>
        </select>
      </label>

      <label className="ps-label">
        Status
        <select
          className="ps-input"
          value={form.status}
          onChange={e => set('status', e.target.value)}
        >
          <option value="SETUP">SETUP</option>
          <option value="ACTIVE">ACTIVE</option>
          <option value="PAUSED">PAUSED</option>
        </select>
      </label>

      {error && <div className="ps-error">{error}</div>}

      <div className="ps-form-actions">
        <button type="button" className="ps-btn ps-btn-secondary" onClick={onCancel} disabled={saving}>
          Cancel
        </button>
        <button type="submit" className="ps-btn ps-btn-primary" disabled={saving}>
          {saving ? 'Saving...' : isEdit ? 'Save Changes' : 'Create Project'}
        </button>
      </div>
    </form>
  );
}

/* ── Main Screen ──────────────────────────────────────────────────────────── */

export default function ProjectsScreen() {
  const { callerEmail } = useUser();
  const [projects, setProjects] = useState([]);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editProject, setEditProject] = useState(null);

  const load = useCallback(() => {
    if (!callerEmail) return;
    setLoading(true);
    setError(null);
    Promise.all([fetchProjects(callerEmail), fetchSites(callerEmail)])
      .then(([proj, sit]) => {
        setProjects(proj || []);
        setSites(sit || []);
      })
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }, [callerEmail]);

  useEffect(() => { load(); }, [load]);

  // Build a site name map for the table
  const siteMap = {};
  for (const s of sites) {
    siteMap[s.site_id || s.id] = s.site_name;
  }

  return (
    <div className="ps-page">
      <div className="ps-header">
        <h1 className="ps-title">Projects</h1>
        <button
          className="ps-btn ps-btn-primary"
          onClick={() => { setEditProject(null); setShowForm(true); }}
          disabled={showForm}
        >
          + New Project
        </button>
      </div>

      {showForm && (
        <ProjectForm
          sites={sites}
          callerEmail={callerEmail}
          editProject={editProject}
          onDone={() => { setShowForm(false); setEditProject(null); load(); }}
          onCancel={() => { setShowForm(false); setEditProject(null); }}
        />
      )}

      {error && <div className="ps-error">{error}</div>}

      {loading ? (
        <div className="ps-loading">Loading projects...</div>
      ) : projects.length === 0 ? (
        <div className="ps-empty">No projects found.</div>
      ) : (
        <div className="ps-table-wrap">
          <table className="ps-table">
            <thead>
              <tr>
                <th>Project Name</th>
                <th>Client OEM</th>
                <th>Site</th>
                <th>Workflow</th>
                <th>Status</th>
                <th>Start Mode</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {projects.map(p => (
                <tr key={p.id}>
                  <td className="ps-cell-name">{p.project_name}</td>
                  <td>{p.client_oem}</td>
                  <td>{siteMap[p.site_id] || p.site_id}</td>
                  <td>{p.workflow_type}</td>
                  <td><StatusBadge status={p.status} /></td>
                  <td>{p.start_mode || '—'}</td>
                  <td>
                    <button
                      className="ps-btn ps-btn-secondary ps-btn-sm"
                      onClick={() => { setEditProject(p); setShowForm(true); }}
                    >
                      Edit
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
